import { logger } from '../utils/logger';

// API Configuration
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  success: boolean;
  data: T;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// Import types
import type { User, UserRole, CreateUserDto, UpdateUserDto, UserListResponse, UserListParams } from '../types/user';
import type { Tenant, TenantStatus, TenantTier, CreateTenantDto, UpdateTenantDto, TenantListResponse, TenantListParams, CreateTenantInvitationDto, CompleteTenantRegistrationDto, TenantInvitationResponse } from '../types/tenant';
import type { ProfileData, ProfileStatistics, UpdateProfileDto, ChangePasswordDto, WabaAccessInfo, CapacityCheck, RecentDispatch } from '../types/profile';
import type { TenantOverview, UserWithStats, AuditLog, AuditLogListParams, DispatchStatsResponse, AdminUserListParams, AdminCampaignListParams, AdminTemplateListParams, DispatchStatsParams, PaginatedResponse } from '../types/admin';
import type { UserWabaAccess, GrantWabaAccessDto, UpdateWabaAccessDto } from '../types/wabaAccess';
import type { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse, UpdateApiKeyRequest, ApiKeyListResponse, ApiKeyResponse, CreateApiKeyFullResponse } from '../types/apiKey';

// Re-export for convenience
export type { User, UserRole, CreateUserDto, UpdateUserDto, UserListResponse, UserListParams };
export type { Tenant, TenantStatus, TenantTier, CreateTenantDto, UpdateTenantDto, TenantListResponse, TenantListParams, CreateTenantInvitationDto, CompleteTenantRegistrationDto, TenantInvitationResponse };
export type { ProfileData, ProfileStatistics, UpdateProfileDto, ChangePasswordDto, WabaAccessInfo, CapacityCheck, RecentDispatch };
export type { TenantOverview, UserWithStats, AuditLog, AuditLogListParams, DispatchStatsResponse, AdminUserListParams, AdminCampaignListParams, AdminTemplateListParams, DispatchStatsParams, PaginatedResponse };
export type { UserWabaAccess, GrantWabaAccessDto, UpdateWabaAccessDto };
export type { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse, UpdateApiKeyRequest, ApiKeyListResponse, ApiKeyResponse, CreateApiKeyFullResponse };

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface RefreshTokenDto {
  refreshToken: string;
}

// WABA Statistics Interface
export interface WabaStatistics {
  statistics: {
    totalMessagesSent: number;
    totalMessagesReceived: number;
    messagesDelivered: number;
    messagesRead: number;
    messagesFailed: number;
    deliveryRate: number;
    readRate: number;
    failureRate: number;
    responseRate: number;
    totalCostBRL: number;
    avgCostPerConversation: number;
    templateCostBRL: number;
    serviceCostBRL: number;
    totalConversations: number;
    marketingConversations: number;
    utilityConversations: number;
    authenticationConversations: number;
    totalDispatches: number;
    successfulDispatches: number;
    failedDispatches: number;
    totalRecipientsReached: number;
    totalErrors: number;
    rateLimitErrors: number;
    invalidPhoneErrors: number;
    templateErrors: number;
    qualityRating: string;
    messagingLimitTier: string;
    qualityRatingChanges: number;
  };
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId: string | null;
  };
}

export interface WabaAccount {
  id: string;
  apiKey: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  tenantId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Novos campos da API
  businessName?: string | null;
  status?: string;
  qualityRating?: string;
  messagingLimitTier?: string;
  maxDailyConversations?: number;
  accountStatus?: string;
  lastQualityCheck?: string;
  // Campo de limite de disparo (0 = ilimitado)
  dailyDispatchLimit?: number;
}

export interface WabaDispatchUsage {
  dailyDispatchLimit: number;
  usedIn24h: number;
  remainingIn24h: number;
  isUnlimited: boolean;
  utilizationPercentage: number;
}

export interface ConnectWabaDto {
  apiKey: string;
  displayPhoneNumber: string;
  tenantId?: string;
}

export interface UpdateWabaStatusDto {
  isActive: boolean;
}

export interface UpdateApiKeyDto {
  apiKey: string;
}

export interface ValidateApiKeyResponse {
  valid: boolean;
  message: string;
}

export interface CreateCampaignDto {
  name: string;
  description?: string;
  wabaAccountId: string;
  templateName: string;
  templateLanguage?: string;
  templateParams?: Record<string, any>;
  type?: 'BROADCAST' | 'TRANSACTIONAL';
  scheduledAt?: string;
}

export interface UpdateCampaignDto {
  name?: string;
  description?: string;
  status?: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
  type?: 'BROADCAST' | 'TRANSACTIONAL';
  scheduledAt?: string;
}

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: string;
  type: string;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  createdAt: string;
  scheduledAt?: string;
}

class ApiClient {
  private baseUrl: string;
  private onTokenRefresh?: (accessToken: string, refreshToken: string, user?: RefreshTokenResponse['user']) => void;
  private onAuthError?: () => void;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  // Método para configurar callbacks
  setCallbacks(callbacks: {
    onTokenRefresh?: (accessToken: string, refreshToken: string, user?: RefreshTokenResponse['user']) => void;
    onAuthError?: () => void;
  }) {
    this.onTokenRefresh = callbacks.onTokenRefresh;
    this.onAuthError = callbacks.onAuthError;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0,
    maxRetries = 3,
    timeoutMs = 10000
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Criar AbortController para timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    // Não definir Content-Type se o body for FormData (deixar o browser definir)
    // Também não definir Content-Type se não houver body
    const isFormData = options.body instanceof FormData;
    const hasBody = options.body !== undefined && options.body !== null;

    const config: RequestInit = {
      ...options,
      headers: {
        ...(isFormData || !hasBody ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      // Handle 204 No Content responses (no body)
      let data: any;
      if (response.status === 204) {
        data = { success: true, statusCode: 204, message: 'Success', data: null as any };
      } else {
        data = await response.json();
      }

      if (!response.ok) {
        // Tratamento especial para erro 401
        if (response.status === 401 && retryCount < maxRetries && !endpoint.includes('/auth/')) {
          logger.log(`[API] 401 detectado - Tentativa ${retryCount + 1} de ${maxRetries} de renovação de token`);

          // Se o erro for jwt malformed, não adianta tentar refresh
          if (data.error?.message?.includes('jwt malformed') || data.message?.includes('jwt malformed')) {
            logger.log('[API] Token malformado - redirecionando para login');
            if (this.onAuthError) {
              this.onAuthError();
            }
            throw new Error('Token inválido. Por favor, faça login novamente.');
          }

          // Tentar fazer refresh do token
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              logger.log('[API] Tentando renovar token com refresh token...');
              const refreshResponse = await this.refreshToken({ refreshToken });

              if (refreshResponse.success) {
                const { accessToken: newAccessToken, refreshToken: newRefreshToken, user: refreshedUser } = refreshResponse.data;

                logger.log('[API] Token renovado com sucesso!');

                // Notificar o contexto sobre os novos tokens e user atualizado
                if (this.onTokenRefresh) {
                  this.onTokenRefresh(newAccessToken, newRefreshToken, refreshedUser);
                }

                // Atualizar o header com o novo token e tentar novamente
                const newHeaders = {
                  ...config.headers,
                  Authorization: `Bearer ${newAccessToken}`,
                };

                // Incrementar o contador de tentativas e tentar novamente
                return this.request<T>(endpoint, { ...options, headers: newHeaders }, retryCount + 1, maxRetries, timeoutMs);
              }
            } catch (refreshError) {
              logger.error('[API] Erro ao renovar token:', refreshError);

              // Se ainda não chegamos ao máximo de tentativas, tentar novamente
              if (retryCount < maxRetries - 1) {
                logger.log(`[API] Tentando novamente... (${retryCount + 2}/${maxRetries})`);
                // Aguardar 500ms antes de tentar novamente
                await new Promise(resolve => setTimeout(resolve, 500));
                return this.request<T>(endpoint, options, retryCount + 1, maxRetries, timeoutMs);
              }

              // Se chegamos ao máximo de tentativas, limpar tudo e notificar
              logger.log('[API] Máximo de tentativas atingido - redirecionando para login');
              if (this.onAuthError) {
                this.onAuthError();
              }
              throw new Error('Sessão expirada. Por favor, faça login novamente.');
            }
          } else {
            logger.log('[API] Refresh token não encontrado - redirecionando para login');
            if (this.onAuthError) {
              this.onAuthError();
            }
            throw new Error('Sessão expirada. Por favor, faça login novamente.');
          }
        }
        // Extract error message from various response formats:
        // - { message: "..." } - Simple format
        // - { error: { message: "..." } } - HttpExceptionFilter format
        const errorMessage = data.error?.message || data.message || 'Something went wrong';
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Tratar erro de timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Tempo de requisição esgotado. Por favor, tente novamente.');
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  async login(
    credentials: LoginCredentials
  ): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async refreshToken(
    data: RefreshTokenDto
  ): Promise<ApiResponse<RefreshTokenResponse>> {
    return this.request<RefreshTokenResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify(data),
    }, 999); // Não tentar refresh novamente se essa chamada falhar (retryCount muito alto)
  }

  async getWabaAccounts(
    token: string,
    tenantId?: string
  ): Promise<ApiResponse<{ wabaAccounts: WabaAccount[] }>> {
    const url = tenantId ? `/api/waba?tenantId=${tenantId}` : '/api/waba';
    return this.request<{ wabaAccounts: WabaAccount[] }>(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getWabaAccount(
    id: string,
    token: string
  ): Promise<ApiResponse<WabaAccount>> {
    return this.request<WabaAccount>(`/api/waba/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async connectWaba(
    data: ConnectWabaDto,
    token: string
  ): Promise<ApiResponse<WabaAccount>> {
    return this.request<WabaAccount>('/api/waba/connect', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateWabaStatus(
    id: string,
    data: UpdateWabaStatusDto,
    token: string
  ): Promise<ApiResponse<WabaAccount>> {
    return this.request<WabaAccount>(`/api/waba/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deleteWabaAccount(
    id: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/waba/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createCampaign(
    data: CreateCampaignDto,
    token: string
  ): Promise<ApiResponse<Campaign>> {
    return this.request<Campaign>('/api/messaging/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async uploadCampaignRecipients(
    campaignId: string,
    file: File,
    token: string
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    // NÃO definir Content-Type manualmente - o browser define automaticamente para multipart/form-data
    return this.request<any>(`/api/messaging/campaigns/${campaignId}/recipients`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // NÃO incluir Content-Type aqui!
      },
      body: formData,
    });
  }

  async updateCampaign(
    campaignId: string,
    data: UpdateCampaignDto,
    token: string
  ): Promise<ApiResponse<Campaign>> {
    return this.request<Campaign>(`/api/messaging/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async startCampaign(
    campaignId: string,
    token: string
  ): Promise<ApiResponse<Campaign>> {
    return this.request<Campaign>(`/api/messaging/campaigns/${campaignId}/start`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async pauseCampaign(
    campaignId: string,
    token: string
  ): Promise<ApiResponse<Campaign>> {
    return this.request<Campaign>(`/api/messaging/campaigns/${campaignId}/pause`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async cancelCampaign(
    campaignId: string,
    token: string
  ): Promise<ApiResponse<Campaign>> {
    return this.request<Campaign>(`/api/messaging/campaigns/${campaignId}/cancel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // CAMPAIGN TEMPLATES
  // ========================================

  async createCampaignTemplate(
    data: import('../types/campaignTemplate').CreateCampaignTemplateDto,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').CampaignTemplate>> {
    return this.request<import('../types/campaignTemplate').CampaignTemplate>(
      '/api/messaging/campaign-templates',
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async getCampaignTemplates(
    params: import('../types/campaignTemplate').TemplateListParams,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').TemplateListResponse>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    // wabaAccountId é obrigatório para isolamento de dados por conta WABA
    queryParams.append('wabaAccountId', params.wabaAccountId);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/messaging/campaign-templates?${queryString}`
      : '/api/messaging/campaign-templates';

    return this.request<import('../types/campaignTemplate').TemplateListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async getCampaignTemplate(
    id: string,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').CampaignTemplate>> {
    return this.request<import('../types/campaignTemplate').CampaignTemplate>(
      `/api/messaging/campaign-templates/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async updateCampaignTemplate(
    id: string,
    data: import('../types/campaignTemplate').UpdateCampaignTemplateDto,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').CampaignTemplate>> {
    return this.request<import('../types/campaignTemplate').CampaignTemplate>(
      `/api/messaging/campaign-templates/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async deleteCampaignTemplate(
    id: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(
      `/api/messaging/campaign-templates/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async executeTemplate(
    templateId: string,
    file: File,
    params: {
      executionName?: string;
      globalParams?: Record<string, any>;
    },
    token: string
  ): Promise<ApiResponse<Campaign>> {
    const formData = new FormData();
    formData.append('recipientsFile', file);

    if (params.executionName) {
      formData.append('executionName', params.executionName);
    }

    if (params.globalParams) {
      try {
        // Sanitize globalParams to ensure all values are properly stringified
        const sanitizedParams: Record<string, any> = {};
        Object.entries(params.globalParams).forEach(([key, value]) => {
          // Convert all values to strings and remove any control characters
          if (value !== null && value !== undefined) {
            const stringValue = String(value)
              .replace(/\r\n/g, ' ') // Remove Windows line breaks
              .replace(/\n/g, ' ')    // Remove Unix line breaks
              .replace(/\r/g, ' ')    // Remove Mac line breaks
              .replace(/\t/g, ' ')    // Remove tabs
              .replace(/[\x00-\x1F\x7F]/g, ''); // Remove other control characters
            sanitizedParams[key] = stringValue.trim();
          }
        });

        const jsonString = JSON.stringify(sanitizedParams);

        // Validate that JSON is properly formed
        JSON.parse(jsonString); // This will throw if invalid

        formData.append('globalParams', jsonString);
      } catch (error) {
        logger.error('Error serializing globalParams:', error, params.globalParams);
        throw new Error(
          'Erro ao processar parâmetros globais. Verifique se não há caracteres especiais inválidos nos campos.'
        );
      }
    }

    return this.request<Campaign>(
      `/api/messaging/campaign-templates/${templateId}/execute`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      },
      0,
      3,
      120000 // 120s timeout for large CSV uploads
    );
  }

  async getTemplateExecutions(
    templateId: string,
    params: import('../types/campaignTemplate').TemplateExecutionsParams,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').TemplateExecutionsResponse>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/messaging/campaign-templates/${templateId}/executions?${queryString}`
      : `/api/messaging/campaign-templates/${templateId}/executions`;

    return this.request<import('../types/campaignTemplate').TemplateExecutionsResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async getTemplateStats(
    templateId: string,
    token: string
  ): Promise<ApiResponse<import('../types/campaignTemplate').TemplateStatsResponse>> {
    return this.request<import('../types/campaignTemplate').TemplateStatsResponse>(
      `/api/messaging/campaign-templates/${templateId}/stats`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  // Dispatch Methods
  async getDispatches(
    params: import('../types/dispatch').DispatchListParams,
    token: string
  ): Promise<import('../types/dispatch').DispatchListResponse> {
    const queryParams = new URLSearchParams();

    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.status) queryParams.append('status', params.status);
    if (params.recipientPhone) queryParams.append('recipientPhone', params.recipientPhone);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const response = await this.request<import('../types/dispatch').DispatchListResponse>(
      `/api/dispatches?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  async getCampaignMetrics(
    campaignId: string,
    token: string
  ): Promise<import('../types/dispatch').CampaignMetrics> {
    const response = await this.request<import('../types/dispatch').CampaignMetrics>(
      `/api/dispatches/campaigns/${campaignId}/metrics`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  async exportCampaignDispatches(campaignId: string, token: string, status?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (status && status !== 'all') {
      params.set('status', status);
    }
    const queryString = params.toString();
    const url = `${this.baseUrl}/api/dispatches/campaigns/${campaignId}/export${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export dispatches');
    }

    return response.blob();
  }

  async getCampaignStats(
    campaignId: string,
    token: string
  ): Promise<ApiResponse<import('../types/dispatch').CampaignStats>> {
    return this.request<import('../types/dispatch').CampaignStats>(
      `/api/messaging/campaigns/${campaignId}/stats`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  // ========================================
  // TENANT MANAGEMENT (SUPER_ADMIN only)
  // ========================================

  async getTenants(
    params: TenantListParams,
    token: string
  ): Promise<ApiResponse<TenantListResponse>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/tenants?${queryString}`
      : '/api/tenants';

    return this.request<TenantListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getAllTenants(
    token: string
  ): Promise<ApiResponse<TenantListResponse>> {
    return this.request<TenantListResponse>('/api/tenants?limit=100', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getTenant(
    id: string,
    token: string
  ): Promise<ApiResponse<Tenant>> {
    return this.request<Tenant>(`/api/tenants/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createTenant(
    data: CreateTenantDto,
    token: string
  ): Promise<ApiResponse<Tenant>> {
    return this.request<Tenant>('/api/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateTenant(
    id: string,
    data: UpdateTenantDto,
    token: string
  ): Promise<ApiResponse<Tenant>> {
    return this.request<Tenant>(`/api/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deleteTenant(
    id: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/tenants/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async inviteUserToTenant(
    data: CreateTenantInvitationDto,
    token: string
  ): Promise<ApiResponse<TenantInvitationResponse>> {
    return this.request<TenantInvitationResponse>('/api/tenants/invite', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // USER MANAGEMENT
  // ========================================

  async getUsers(
    params: UserListParams,
    token: string
  ): Promise<ApiResponse<UserListResponse>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.active !== undefined) queryParams.append('active', params.active.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/users?${queryString}`
      : '/api/users';

    return this.request<UserListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getUser(
    id: string,
    token: string
  ): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/users/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createUser(
    data: CreateUserDto,
    token: string
  ): Promise<ApiResponse<User>> {
    return this.request<User>('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateUser(
    id: string,
    data: UpdateUserDto,
    token: string,
    tenantId?: string
  ): Promise<ApiResponse<User>> {
    const url = tenantId
      ? `/api/users/${id}?tenantId=${tenantId}`
      : `/api/users/${id}`;

    return this.request<User>(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deleteUser(
    id: string,
    token: string,
    tenantId?: string
  ): Promise<ApiResponse<void>> {
    const url = tenantId
      ? `/api/users/${id}?tenantId=${tenantId}`
      : `/api/users/${id}`;

    return this.request<void>(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // WABA STATISTICS
  // ========================================

  async getWabaStatistics(
    wabaId: string,
    token: string
  ): Promise<ApiResponse<WabaStatistics>> {
    return this.request<WabaStatistics>(`/api/waba/${wabaId}/statistics/summary`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // API KEY MANAGEMENT
  // ========================================

  async validateApiKey(
    apiKey: string,
    token: string
  ): Promise<ApiResponse<ValidateApiKeyResponse>> {
    return this.request<ValidateApiKeyResponse>('/api/waba/validate-api-key', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ apiKey }),
    });
  }

  async updateWabaApiKey(
    wabaId: string,
    apiKey: string,
    token: string
  ): Promise<ApiResponse<{ waba: WabaAccount }>> {
    return this.request<{ waba: WabaAccount }>(`/api/waba/${wabaId}/api-key`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ apiKey }),
    });
  }

  // ========================================
  // PROFILE ENDPOINTS (self-service)
  // ========================================

  /**
   * Get current user's profile with statistics
   */
  async getProfile(token: string): Promise<ApiResponse<ProfileData>> {
    return this.request<ProfileData>('/api/profile', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get current user's statistics only
   */
  async getProfileStatistics(token: string): Promise<ApiResponse<ProfileStatistics>> {
    return this.request<ProfileStatistics>('/api/profile/statistics', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update current user's profile (name/email)
   */
  async updateProfile(
    data: UpdateProfileDto,
    token: string
  ): Promise<ApiResponse<Omit<User, 'password'>>> {
    return this.request<Omit<User, 'password'>>('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Change current user's password
   */
  async changePassword(
    data: ChangePasswordDto,
    token: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return this.request<{ success: boolean; message: string }>('/api/profile/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get current user's WABA access with usage info
   */
  async getMyWabaAccess(token: string): Promise<ApiResponse<WabaAccessInfo[]>> {
    return this.request<WabaAccessInfo[]>('/api/profile/waba-access', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Check dispatch capacity for a specific WABA
   */
  async checkDispatchCapacity(
    wabaAccountId: string,
    count: number,
    token: string
  ): Promise<ApiResponse<CapacityCheck>> {
    return this.request<CapacityCheck>(`/api/profile/waba-access/${wabaAccountId}/capacity/${count}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get current user's recent campaigns
   */
  async getMyRecentCampaigns(
    limit: number = 10,
    token: string
  ): Promise<ApiResponse<Campaign[]>> {
    return this.request<Campaign[]>(`/api/profile/recent-campaigns?limit=${limit}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get current user's recent dispatch activity
   */
  async getMyRecentDispatches(
    limit: number = 20,
    token: string
  ): Promise<ApiResponse<RecentDispatch[]>> {
    return this.request<RecentDispatch[]>(`/api/profile/recent-dispatches?limit=${limit}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // ADMIN ENDPOINTS (ADMIN/SUPER_ADMIN only)
  // ========================================

  /**
   * Get admin overview with tenant stats
   */
  async getAdminOverview(token: string): Promise<ApiResponse<TenantOverview>> {
    return this.request<TenantOverview>('/api/admin/overview', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get all users with statistics (admin view)
   */
  async getAdminUsers(
    params: AdminUserListParams,
    token: string
  ): Promise<ApiResponse<PaginatedResponse<UserWithStats>>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/admin/users?${queryString}`
      : '/api/admin/users';

    return this.request<PaginatedResponse<UserWithStats>>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get specific user statistics (admin view)
   */
  async getAdminUserStats(
    userId: string,
    token: string
  ): Promise<ApiResponse<UserWithStats>> {
    return this.request<UserWithStats>(`/api/admin/users/${userId}/stats`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get all campaigns (admin view)
   */
  async getAdminCampaigns(
    params: AdminCampaignListParams,
    token: string
  ): Promise<ApiResponse<PaginatedResponse<Campaign>>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/admin/campaigns?${queryString}`
      : '/api/admin/campaigns';

    return this.request<PaginatedResponse<Campaign>>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get all templates (admin view)
   */
  async getAdminTemplates(
    params: AdminTemplateListParams,
    token: string
  ): Promise<ApiResponse<PaginatedResponse<import('../types/campaignTemplate').CampaignTemplate>>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.userId) queryParams.append('userId', params.userId);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/admin/templates?${queryString}`
      : '/api/admin/templates';

    return this.request<PaginatedResponse<import('../types/campaignTemplate').CampaignTemplate>>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get dispatch statistics (admin view)
   */
  async getAdminDispatchStats(
    params: DispatchStatsParams,
    token: string
  ): Promise<ApiResponse<DispatchStatsResponse>> {
    const queryParams = new URLSearchParams();

    if (params.period) queryParams.append('period', params.period);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/admin/dispatch-stats?${queryString}`
      : '/api/admin/dispatch-stats';

    return this.request<DispatchStatsResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get audit logs
   */
  async getAuditLogs(
    params: AuditLogListParams,
    token: string
  ): Promise<ApiResponse<PaginatedResponse<AuditLog>>> {
    const queryParams = new URLSearchParams();

    // Backend expects offset, not page - convert page to offset
    if (params.page) {
      const offset = ((params.page || 1) - 1) * (params.limit || 20);
      queryParams.append('offset', offset.toString());
    }
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.action) queryParams.append('action', params.action);
    if (params.actorId) queryParams.append('actorId', params.actorId);
    if (params.targetType) queryParams.append('targetType', params.targetType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/admin/audit-logs?${queryString}`
      : '/api/admin/audit-logs';

    return this.request<PaginatedResponse<AuditLog>>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // WABA ACCESS CONTROL (ADMIN/SUPER_ADMIN)
  // ========================================

  /**
   * Grant WABA access to a user
   */
  async grantWabaAccess(
    data: GrantWabaAccessDto,
    token: string
  ): Promise<ApiResponse<UserWabaAccess>> {
    return this.request<UserWabaAccess>('/api/waba/access/grant', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Revoke WABA access from a user
   */
  async revokeWabaAccess(
    userId: string,
    wabaAccountId: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/waba/access/${userId}/${wabaAccountId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update WABA access settings (daily limit, active status)
   */
  async updateWabaAccess(
    userId: string,
    wabaAccountId: string,
    data: UpdateWabaAccessDto,
    token: string
  ): Promise<ApiResponse<UserWabaAccess>> {
    return this.request<UserWabaAccess>(`/api/waba/access/${userId}/${wabaAccountId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get all WABA access records for a user
   */
  async getUserWabaAccess(
    userId: string,
    includeInactive: boolean = false,
    token: string
  ): Promise<ApiResponse<UserWabaAccess[]>> {
    const endpoint = `/api/waba/access/user/${userId}?includeInactive=${includeInactive}`;
    return this.request<UserWabaAccess[]>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get all users with access to a specific WABA
   */
  async getWabaUsers(
    wabaAccountId: string,
    includeInactive: boolean = false,
    token: string
  ): Promise<ApiResponse<UserWabaAccess[]>> {
    const endpoint = `/api/waba/access/waba/${wabaAccountId}?includeInactive=${includeInactive}`;
    return this.request<UserWabaAccess[]>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Bulk grant WABA access to multiple users
   */
  async bulkGrantWabaAccess(
    wabaAccountId: string,
    userIds: string[],
    dailyLimit: number = 0,
    token: string
  ): Promise<ApiResponse<{ granted: number; failed: number; results: UserWabaAccess[] }>> {
    return this.request<{ granted: number; failed: number; results: UserWabaAccess[] }>(
      '/api/waba/access/bulk-grant',
      {
        method: 'POST',
        body: JSON.stringify({ wabaAccountId, userIds, dailyLimit }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Reset daily usage counter for a user's WABA access
   */
  async resetWabaUsage(
    userId: string,
    wabaAccountId: string,
    token: string
  ): Promise<ApiResponse<{ success: boolean; message: string; deletedCount: number }>> {
    return this.request<{ success: boolean; message: string; deletedCount: number }>(
      `/api/waba/access/${userId}/${wabaAccountId}/reset-usage`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  // ========================================
  // WABA DISPATCH LIMIT (SUPER_ADMIN only)
  // ========================================

  /**
   * Get dispatch usage statistics for a WABA account
   * Available to all users with WABA access
   */
  async getWabaDispatchUsage(
    wabaId: string,
    token: string
  ): Promise<ApiResponse<WabaDispatchUsage>> {
    return this.request<WabaDispatchUsage>(`/api/waba/${wabaId}/dispatch-usage`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update WABA account daily dispatch limit
   * SUPER_ADMIN only
   * @param dailyDispatchLimit - 0 means unlimited
   */
  async updateWabaDispatchLimit(
    wabaId: string,
    dailyDispatchLimit: number,
    token: string
  ): Promise<ApiResponse<{ wabaAccountId: string; dailyDispatchLimit: number; isUnlimited: boolean }>> {
    return this.request<{ wabaAccountId: string; dailyDispatchLimit: number; isUnlimited: boolean }>(
      `/api/waba/${wabaId}/dispatch-limit`,
      {
        method: 'PATCH',
        body: JSON.stringify({ dailyDispatchLimit }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Reset WABA account dispatch usage (delete logs from last 24h)
   * SUPER_ADMIN only
   */
  async resetWabaDispatchUsage(
    wabaId: string,
    token: string
  ): Promise<ApiResponse<{ deletedCount: number }>> {
    return this.request<{ deletedCount: number }>(
      `/api/waba/${wabaId}/reset-dispatch-usage`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  // ========================================
  // API KEYS MANAGEMENT (SUPER_ADMIN only)
  // ========================================

  /**
   * List all API keys (optionally filtered by tenant)
   */
  async getApiKeys(
    token: string,
    tenantId?: string
  ): Promise<ApiResponse<ApiKey[]>> {
    const queryParams = new URLSearchParams();
    if (tenantId) queryParams.append('tenantId', tenantId);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/api-keys?${queryString}`
      : '/api/api-keys';

    return this.request<ApiKey[]>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get a specific API key by ID
   */
  async getApiKey(
    id: string,
    token: string
  ): Promise<ApiResponse<ApiKey>> {
    return this.request<ApiKey>(`/api/api-keys/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Create a new API key
   * Returns the raw key only once - must be saved by the user
   */
  async createApiKey(
    data: CreateApiKeyRequest,
    token: string
  ): Promise<ApiResponse<CreateApiKeyResponse>> {
    return this.request<CreateApiKeyResponse>('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update an API key (name, description, isActive, expiresAt)
   */
  async updateApiKey(
    id: string,
    data: UpdateApiKeyRequest,
    token: string
  ): Promise<ApiResponse<ApiKey>> {
    return this.request<ApiKey>(`/api/api-keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Revoke an API key (mark as revoked, cannot be undone)
   */
  async revokeApiKey(
    id: string,
    token: string
  ): Promise<ApiResponse<ApiKey>> {
    return this.request<ApiKey>(`/api/api-keys/${id}/revoke`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Permanently delete an API key
   */
  async deleteApiKey(
    id: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/api-keys/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // ========================================
  // BILLING ENDPOINTS
  // ========================================

  /**
   * Get tenant billing dashboard data
   * ADMIN: uses token's tenantId
   * SUPER_ADMIN: must specify tenantId
   */
  async getTenantBilling(
    token: string,
    tenantId?: string
  ): Promise<ApiResponse<import('../types/billing').TenantBillingData>> {
    const queryParams = new URLSearchParams();
    if (tenantId) queryParams.append('tenantId', tenantId);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/billing/tenant-billing?${queryString}`
      : '/api/billing/tenant-billing';

    return this.request<import('../types/billing').TenantBillingData>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * List invoices with filters
   */
  async getInvoices(
    params: import('../types/billing').InvoiceFilterParams,
    token: string
  ): Promise<ApiResponse<import('../types/billing').InvoiceListResponse>> {
    const queryParams = new URLSearchParams();

    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.status) queryParams.append('status', params.status);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.includeBreakdown) queryParams.append('includeBreakdown', params.includeBreakdown.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/billing/invoices?${queryString}`
      : '/api/billing/invoices';

    return this.request<import('../types/billing').InvoiceListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get a single invoice by ID
   */
  async getInvoice(
    invoiceId: string,
    token: string,
    includeBreakdown: boolean = false
  ): Promise<ApiResponse<import('../types/billing').Invoice>> {
    const endpoint = `/api/billing/invoices/${invoiceId}?includeBreakdown=${includeBreakdown}`;
    return this.request<import('../types/billing').Invoice>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update invoice status (SUPER_ADMIN only)
   */
  async updateInvoiceStatus(
    invoiceId: string,
    data: import('../types/billing').UpdateInvoiceStatusDto,
    token: string
  ): Promise<ApiResponse<import('../types/billing').Invoice>> {
    return this.request<import('../types/billing').Invoice>(`/api/billing/invoices/${invoiceId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Generate invoice for a tenant (SUPER_ADMIN only)
   */
  async generateInvoice(
    tenantId: string,
    data: import('../types/billing').GenerateInvoiceDto,
    token: string
  ): Promise<ApiResponse<import('../types/billing').Invoice>> {
    return this.request<import('../types/billing').Invoice>(`/api/billing/invoices/generate/${tenantId}`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Close current billing period for a tenant (SUPER_ADMIN only)
   */
  async closeCurrentPeriod(
    tenantId: string,
    token: string
  ): Promise<ApiResponse<import('../types/billing').Invoice | null>> {
    return this.request<import('../types/billing').Invoice | null>(`/api/billing/invoices/close/${tenantId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Delete an invoice (SUPER_ADMIN only)
   * Note: Paid invoices cannot be deleted
   */
  async deleteInvoice(
    invoiceId: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/billing/invoices/${invoiceId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Get tenants dropdown for SUPER_ADMIN
   */
  async getBillingTenantsDropdown(
    token: string
  ): Promise<ApiResponse<import('../types/billing').TenantDropdownItem[]>> {
    return this.request<import('../types/billing').TenantDropdownItem[]>('/api/billing/tenants-dropdown', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Update tenant billing cycle day (SUPER_ADMIN only)
   */
  async updateTenantBillingCycle(
    tenantId: string,
    data: import('../types/billing').UpdateBillingCycleDto,
    token: string
  ): Promise<ApiResponse<{ id: string; name: string; billingCycleDay: number }>> {
    return this.request<{ id: string; name: string; billingCycleDay: number }>(
      `/api/tenants/${tenantId}/billing-cycle`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  // ========================================
  // NOTIFICATIONS
  // ========================================

  /**
   * Get notifications for the current user (global + tenant)
   */
  async getNotifications(
    token: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ApiResponse<import('../types/notification').NotificationListResponse>> {
    return this.request<import('../types/notification').NotificationListResponse>(
      `/api/notifications?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Get ALL notifications (super_admin management view)
   */
  async getAllNotifications(
    token: string,
    page: number = 1,
    limit: number = 15
  ): Promise<ApiResponse<import('../types/notification').NotificationListResponse>> {
    return this.request<import('../types/notification').NotificationListResponse>(
      `/api/notifications/all?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Get unread notification count for the current user
   */
  async getUnreadNotificationCount(
    token: string
  ): Promise<ApiResponse<import('../types/notification').UnreadCountResponse>> {
    return this.request<import('../types/notification').UnreadCountResponse>(
      '/api/notifications/unread-count',
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Mark a single notification as read
   */
  async markNotificationAsRead(
    id: string,
    token: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/api/notifications/${id}/read`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead(
    token: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      '/api/notifications/read-all',
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  /**
   * Create a notification (super_admin only)
   */
  async createNotification(
    data: import('../types/notification').CreateNotificationDto,
    token: string
  ): Promise<ApiResponse<import('../types/notification').Notification>> {
    return this.request<import('../types/notification').Notification>(
      '/api/notifications',
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }
}

export const apiClient = new ApiClient();
